// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { TextArea } from 'informed';

import {
  deleteChronicProblem,
  addChronicProblem,
  updateChronicProblem,
  deleteAcuteProblem,
  addAcuteProblem,
  updateAcuteProblem,
} from '../../../actions/problemActions';

import DateText from '../../DateText.jsx';
import Table from '../../Table.jsx';
import Modal from '../../Modal.jsx';
import { Button, PrimaryButton } from '../../Buttons.jsx';
import Typeahead from '../../Typeahead.jsx';

import ProblemTreatmentModal from './ProblemTreatmentModal.jsx';

type Props = {
  chronicProblems: Object[],
  acuteProblems: Object[],
  problemIdToMeds: Object,
  problemIdToInterventions: Object,
  activePatientId: string,
  addChronicProblem: Function,
  deleteChronicProblem: Function,
  updateChronicProblem: Function,
  addAcuteProblem: Function,
  deleteAcuteProblem: Function,
  updateAcuteProblem: Function,
  current: boolean,
  resolved: boolean,
  acute: boolean,
  chronic: boolean,
}

/*
  Modal only used on Add New Patient page, where treatment modal used so can stay on same page.
  All modal code only relevant in this case.
*/
type State = {
  modalOpen: bool,
  modalRow: ?Object,
}

class ProblemTableComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      modalOpen: false,
      modalRow: null,
    };
  }

  handleOpenModal = (row: Object) => () => {
    this.setState({
      modalOpen: true,
      modalRow: row,
    });
  }

  closeModal = () => {
    this.setState({
      modalOpen: false,
      modalRow: null,
    });
  }

  /*
    Use a function to update modal's open problem on intervention update,
    otherwise cached modalRow not updated
  */
  getCurrentProblem = () => {
    if (this.state.modalRow) {
      if (this.props.acute) {
        // $FlowFixMe - there is check for if modalRow exists
        return this.props.acuteProblems.find(problem => problem._id === this.state.modalRow._id);
      }
      if (this.props.chronic) {
        // $FlowFixMe - there is check for if modalRow exists
        return this.props.chronicProblems.find(problem => problem._id === this.state.modalRow._id);
      }
    }
    return null;
  }

  actionsColumn = (row: Object) => (
    <div>
      {(this.props.current || this.props.resolved)
        ? <Link to={`./progressNotes/${row._id}`}>
          <Button>Treatments & Chart</Button>
        </Link>
        : <PrimaryButton onClick={this.handleOpenModal(row)}>
          Manage Treatments
        </PrimaryButton>
      }
    </div>
  );

  /* eslint-disable no-param-reassign */
  transformDates(formValues) {
    formValues.onsetDate = moment(formValues.onsetDate, 'DD-MM-YYYY').toISOString();
    formValues.resolutionDate = moment(formValues.resolutionDate, 'DD-MM-YYYY').toISOString();
  }

  handleDeleteRow = (id: string, row: Object) => {
    if (this.props.acute || row.acute) {
      this.props.deleteAcuteProblem(this.props.activePatientId, id);
    } else if (this.props.chronic || row.chronic) {
      this.props.deleteChronicProblem(this.props.activePatientId, id);
    }
  }

  handleSaveRow = (id: string, formData: Object, row: Object) => {
    this.transformDates(formData.values);
    if (this.props.acute || row.acute) {
      this.props.updateAcuteProblem(this.props.activePatientId,
        {
          problemId: id,
          ...formData.values,
        });
    } else if (this.props.chronic || row.chronic) {
      this.props.updateChronicProblem(this.props.activePatientId,
        {
          problemId: id,
          ...formData.values,
        });
    }
  }

  handleSaveNewRow = (formData: Object) => {
    this.transformDates(formData.values);
    if (this.props.acute) {
      this.props.addAcuteProblem(this.props.activePatientId, formData.values);
    } else if (this.props.chronic) {
      this.props.addChronicProblem(this.props.activePatientId, formData.values);
    }
  }

  render() {
    const { problemIdToMeds, problemIdToInterventions } = this.props;

    const columns = [
      {
        Header: 'Date of Onset',
        id: 'onsetDate',
        cell: item => (item.onsetDate ? moment(item.onsetDate).format() : '--'),
        editCell: item => <DateText field='onsetDate' item={item} />,
        width: 100,
      },
      {
        Header: 'Date of Resolution',
        id: 'resolutionDate',
        cell: item => (item.resolutionDate ? moment(item.resolutionDate).format() : '(ongoing)'),
        editCell: item => <DateText field="resolutionDate" item={item} nullable />,
        width: 130,
      },
      {
        Header: 'Diagnosis',
        id: 'diagnosis',
        editCell: item => <Typeahead field='diagnosis' initialValue={item.diagnosis} endpoint='diagnosis' />,
      },
      {
        Header: 'Details',
        id: 'details',
        editCell: item => <TextArea field='details' initialValue={item.details} />,
      },
      {
        Header: this.props.current ? 'Current Treatments' : 'Treatments',
        id: 'treatments',
        cell: (item) => {
          let treatments = [];
          if (item._id in problemIdToMeds) {
            let meds = problemIdToMeds[item._id];
            if (this.props.current) {
              meds = meds.filter(med => !med.endDate || moment(med.endDate) > moment());
            }
            treatments = treatments.concat(meds.map(
              med => <div key={med._id}>{med.drugName}</div>,
            ));
          }
          if (item._id in problemIdToInterventions) {
            let interventions = problemIdToInterventions[item._id];
            if (this.props.current) {
              interventions = interventions.filter(intervention => !intervention.endDate || moment(intervention.endDate) > moment());
            }
            treatments = treatments.concat(interventions.map(
              intervention => <div key={intervention._id}>{intervention.name}</div>,
            ));
          }
          return treatments;
        },
      },
    ];

    let problems = [];
    let { acuteProblems, chronicProblems } = this.props;
    if (this.props.acute) {
      problems = acuteProblems;
    } else if (this.props.chronic) {
      problems = chronicProblems;
    } else {
      acuteProblems = acuteProblems.map(problem => ({ ...problem, acute: true }));
      chronicProblems = chronicProblems.map(problem => ({ ...problem, chronic: true }));
      problems = acuteProblems.concat(chronicProblems);
    }

    if (this.props.current) {
      problems = problems.filter(problem => !problem.resolutionDate);
    } else if (this.props.resolved) {
      problems = problems.filter(problem => problem.resolutionDate);
    }

    problems = problems.sort((a, b) => {
      if (a.resolutionDate && !b.resolutionDate) {
        return 1;
      }
      if (b.resolutionDate && !a.resolutionDate) {
        return -1;
      }
      if (!a.onsetDate) {
        return 1;
      }
      if (!b.onsetDate) {
        return -1;
      }
      if (a.onsetDate < b.onsetDate) {
        return 1;
      }
      if (b.onsetDate < a.onsetDate) {
        return -1;
      }
      return 0;
    });

    return (
      <div>
        <Table
          columns={columns}
          data={problems}
          handleDeleteRow={this.handleDeleteRow}
          handleSaveRow={this.handleSaveRow}
          handleSaveNewRow={(this.props.acute || this.props.chronic) && this.handleSaveNewRow}
          extraActions={this.actionsColumn}
          actionsWidth={150}
          deleteText={'Deleting a diagnosis will delete all associated treatments and progress notes'}
        />
        <Modal isOpen={this.state.modalOpen}>
          <ProblemTreatmentModal
            closeModal={this.closeModal}
            problem={this.getCurrentProblem() || {}}
            problemIdToMeds={problemIdToMeds}
            problemIdToInterventions={problemIdToInterventions}
            acute={this.props.acute}
            chronic={this.props.chronic}
          />
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  chronicProblems: state.patient.data.chronicProblems,
  acuteProblems: state.patient.data.acuteProblems,
  problemIdToMeds: state.patient.data.problemIdToMeds,
  problemIdToInterventions: state.patient.data.problemIdToInterventions,
  activePatientId: state.patient.data.activePatientId,
});

const mapDispatchToProps = dispatch => ({
  addChronicProblem: (patientId, problem) => dispatch(addChronicProblem(patientId, problem)),
  deleteChronicProblem:
    (patientId, problemId) => dispatch(deleteChronicProblem(patientId, problemId)),
  updateChronicProblem: (patientId, problem) => dispatch(updateChronicProblem(patientId, problem)),
  addAcuteProblem: (patientId, problem) => dispatch(addAcuteProblem(patientId, problem)),
  deleteAcuteProblem:
    (patientId, problemId) => dispatch(deleteAcuteProblem(patientId, problemId)),
  updateAcuteProblem: (patientId, problem) => dispatch(updateAcuteProblem(patientId, problem)),
});

const ProblemTable = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProblemTableComponent);

export default ProblemTable;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/problem/ProblemTable.jsx